/**
 * FinalPackConfirmModal.jsx
 *
 * RC 1.49.x — Final Pack Confirmation (operator-locked, 2026-05-06).
 *
 * A blocking confirmation rendered between the Step 4 "Create
 * Application Pack" click and the actual POST /api/application-packs.
 * It enumerates exactly what the user will get based on the current
 * run state, and reaffirms — visibly, every time — that the uploaded
 * Source CV is private reference material and is never exported.
 *
 * Composition signals (passed in by the wizard, single source of
 * truth for "what's in the pack"):
 *   • hasTailoredCv     — selectedCv is a cv_variant
 *   • hasCoverLetter    — a cover letter was generated this flow
 *   • hasVariantFit     — Step 3 produced a Variant Fit on the
 *                         selected Tailored CV
 *   • hasBaselineAnchor — Source-CV anchor (always true for a real
 *                         pack — Source CV is mandatory at onboarding;
 *                         Interview Intelligence is therefore always
 *                         included)
 *
 * Operator rules pinned here:
 *   • Cover Sheet line is "included" only when hasTailoredCv AND
 *     hasVariantFit are both true.
 *   • Source CV line is ALWAYS "Never included" — copy is locked.
 *
 * No backend, scoring, billing or export-logic implications. Pure
 * UI gate.
 */

import { useEffect, useMemo } from 'react';
import { Check, X, ShieldCheck, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

function Row({ included, label, hint, testid }) {
  return (
    <li
      data-testid={testid}
      className="flex items-start gap-2.5"
    >
      <span
        className={
          'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full shrink-0 ' +
          (included
            ? 'bg-ndor-success-light text-ndor-success'
            : 'bg-ndor-surface-alt text-ndor-ink-muted border border-ndor-edge')
        }
      >
        {included ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      </span>
      <div className="text-sm leading-relaxed">
        <span
          className={
            included
              ? 'text-ndor-ink font-medium'
              : 'text-ndor-ink-muted line-through'
          }
        >
          {label}
        </span>
        {hint && (
          <span className="block text-xs text-ndor-ink-muted mt-0.5">
            {hint}
          </span>
        )}
      </div>
    </li>
  );
}

export default function FinalPackConfirmModal({
  open,
  onOpenChange,
  composition,
  creating,
  onCancel,
  onConfirm,
}) {
  const hasTailoredCv     = !!composition?.hasTailoredCv;
  const hasCoverLetter    = !!composition?.hasCoverLetter;
  const hasVariantFit     = !!composition?.hasVariantFit;
  // RC 1.49.x EXPORT_RULES (operator-locked, 2026-05-06):
  //   Interview Intelligence + Cover Sheet BOTH require a Tailored
  //   CV AND Variant Fit. Baseline Fit is diagnostic only and does
  //   NOT generate Interview Intelligence.
  const hasIntelligence   = hasTailoredCv && hasVariantFit;
  const hasCoverSheet     = hasTailoredCv && hasVariantFit;
  // A pack with no Tailored CV and no Cover Letter is NOT
  // exportable. The modal switches to a blocked-state explainer.
  const isBaselineOnlyPack = !hasTailoredCv && !hasCoverLetter;

  // Defensive — a stray escape-key dismiss should not fire onConfirm.
  useEffect(() => {
    if (!open) return undefined;
    return () => {};
  }, [open]);

  const summaryWord = useMemo(() => {
    if (isBaselineOnlyPack) return 'baseline-only flow';
    if (hasIntelligence && hasCoverLetter) return 'full pack';
    if (hasIntelligence) return 'intelligence pack';
    if (hasTailoredCv && hasCoverLetter) return 'tailored-CV pack with cover letter';
    if (hasTailoredCv) return 'tailored-CV pack';
    if (hasCoverLetter) return 'cover-letter pack';
    return 'pack';
  }, [isBaselineOnlyPack, hasIntelligence, hasTailoredCv, hasCoverLetter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto"
        data-testid="final-pack-confirm-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-ndor-gold" />
            Confirm pack contents
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review exactly what your application pack will contain before it
            is created. Your uploaded Source CV is private reference material
            and is never exported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isBaselineOnlyPack ? (
            <div
              className="space-y-3"
              data-testid="final-pack-confirm-baseline-only-block"
            >
              <p className="text-sm text-ndor-ink leading-relaxed">
                <span className="font-medium">Baseline Fit is diagnostic only.</span>{' '}
                It tells you where your Source CV stands against this role, but it
                does NOT produce an exportable intelligence pack.
              </p>
              <p className="text-sm text-ndor-ink-secondary leading-relaxed">
                To create an exportable pack, generate a Tailored CV and run
                Variant Fit on it. You can also add a generated Cover Letter.
              </p>
              <ul
                className="space-y-2 pl-1"
                data-testid="final-pack-confirm-baseline-only-checklist"
              >
                <Row
                  included={false}
                  label="Tailored CV"
                  hint="Required — generate one in Step 1."
                  testid="final-pack-confirm-row-tailored-cv"
                />
                <Row
                  included={false}
                  label="Variant Fit"
                  hint="Required — run on the tailored CV in Step 3."
                  testid="final-pack-confirm-row-variant-fit"
                />
              </ul>
            </div>
          ) : (
            <>
              <p
                className="text-sm text-ndor-ink-secondary leading-relaxed"
                data-testid="final-pack-confirm-summary"
              >
                You're about to create a <span className="text-ndor-ink font-medium">{summaryWord}</span> for this job.
              </p>

              <ul
                className="space-y-2.5"
                data-testid="final-pack-confirm-composition"
              >
                <Row
                  included={hasTailoredCv}
                  label="Tailored CV"
                  hint={hasTailoredCv ? 'Generated this flow.' : 'Not generated this flow.'}
                  testid="final-pack-confirm-row-tailored-cv"
                />
                <Row
                  included={hasCoverLetter}
                  label="Cover letter"
                  hint={hasCoverLetter ? 'Generated this flow.' : 'Not generated this flow.'}
                  testid="final-pack-confirm-row-cover-letter"
                />
                <Row
                  included={hasIntelligence}
                  label="Interview Intelligence"
                  hint={
                    hasIntelligence
                      ? 'Strengths, risks, talking points and likely interview questions.'
                      : 'Excluded — requires a tailored CV AND a Variant Fit run.'
                  }
                  testid="final-pack-confirm-row-interview-intelligence"
                />
                <Row
                  included={hasCoverSheet}
                  label="Variant-Fit cover page"
                  hint={
                    hasCoverSheet
                      ? 'Included because you generated a tailored CV and ran Variant Fit.'
                      : 'Excluded — requires a tailored CV AND a Variant Fit run.'
                  }
                  testid="final-pack-confirm-row-cover-sheet"
                />
                <Row
                  included={false}
                  label="Source CV"
                  hint="Never included, never exported, never previewed. Private reference material only."
                  testid="final-pack-confirm-row-source-cv"
                />
              </ul>
            </>
          )}

          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5 text-xs text-ndor-ink-secondary leading-relaxed"
            data-testid="final-pack-confirm-source-cv-disclaimer"
          >
            Your pack includes only what you generated or analysed in this flow.
            Your uploaded Source CV is private reference material only and is
            never exported. NDORFlow never auto-applies — final submission is
            always your decision.
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 sticky bottom-0 -mx-6 px-6 -mb-6 pb-6 pt-3 bg-background border-t border-ndor-edge/40">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={creating}
            data-testid="final-pack-confirm-cancel"
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={creating || isBaselineOnlyPack}
            data-testid="final-pack-confirm-create"
            className="flex-1 sm:flex-none bg-ndor-gold text-ndor-ink hover:bg-ndor-gold/90 font-medium"
            title={isBaselineOnlyPack
              ? 'Generate a Tailored CV and run Variant Fit to create an exportable pack.'
              : undefined}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Creating…
              </>
            ) : (
              'Create Pack'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
